// Consent adapter — resolves the visitor's consent state from the host page
// (ANALYTICS-SYSTEM-DESIGN.md §3.2).
//
// Resolution order:
//   1. explicit override (data-3dconfig-consent attr / init option)
//   2. IAB TCF v2.2                (__tcfapi)
//   3. Google Consent Mode v2      (dataLayer analytics_storage)
//   4. Cookiebot
//   5. Cookie Information (DK)
//   6. OneTrust
//   7. Usercentrics (best effort)
//   8. custom adapters from bootstrap config (detectExpr / readExpr)
//   9. fallback per config: banner | cookieless | essential
//
// GPC / DNT: when respected, the state is hard-capped at cookieless — a CMP
// grant does not upgrade, and our banner is never shown.
//
// Every probe into the host page is wrapped in try/catch: the loader may run
// inside cross-origin iframes, under strict CSP, or against half-loaded CMPs.
// A failed probe is treated as "not present" and never throws outward.

import { readStoredBannerChoice, showConsentBanner, type BannerHandle } from "./banner";
import type {
  ConsentConfig,
  ConsentState,
  ConsentStatus,
  CustomAdapterConfig,
} from "./types";

export const VISITOR_ID_KEY = "dconfig-vid";

/** Detection retry schedule (ms after start). CMP scripts usually load after
 *  us; a short grace period avoids flashing our fallback banner on pages that
 *  DO have a CMP that just hasn't booted yet. */
const DETECT_RETRY_AT_MS = [300, 800, 1500, 3000];

export interface ConsentAdapterOptions {
  config?: ConsentConfig;
  /** data-3dconfig-consent attribute / init option. */
  explicitOverride?: string | null;
  /** Short language code for the fallback banner. */
  language?: string;
  /** API origin for POST /api/consent/record (own-banner choices only). */
  apiBase?: string;
  sessionId?: string | null;
  /** Mount point for the fallback banner (embed container). */
  bannerContainer?: HTMLElement | null;
  onChange?: (state: ConsentState) => void;
}

export interface ConsentAdapter {
  getState(): ConsentState;
  /** Persistent visitor id — non-null only while consent is granted. */
  getVisitorId(): string | null;
  /** Programmatic override (window.DConfigAnalytics.setConsent / JS API). */
  setConsent(status: ConsentStatus, source?: string): void;
  onChange(cb: (state: ConsentState) => void): () => void;
  destroy(): void;
}

// ── small safe-environment helpers ──────────────────────────────────────

type AnyWindow = Window & Record<string, any>;

function win(): AnyWindow | null {
  try {
    return typeof window !== "undefined" ? (window as AnyWindow) : null;
  } catch {
    return null;
  }
}

function safeGetItem(key: string): string | null {
  try {
    return win()?.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    win()?.localStorage?.setItem(key, value);
  } catch {
    /* storage unavailable */
  }
}

function safeRemoveItem(key: string): void {
  try {
    win()?.localStorage?.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}

function newUuid(): string {
  try {
    const c = win()?.crypto ?? (typeof crypto !== "undefined" ? crypto : undefined);
    if (c?.randomUUID) return c.randomUUID();
    if (c?.getRandomValues) {
      const bytes = new Uint8Array(16);
      c.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
  } catch {
    /* fall through */
  }
  // Math.random fallback — not cryptographically strong, fine for an analytics id.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** GPC or DNT set → cap at cookieless (design §3.1). */
export function isGpcOrDntActive(): boolean {
  try {
    const w = win();
    const nav = (w?.navigator ?? (typeof navigator !== "undefined" ? navigator : undefined)) as any;
    if (!nav) return false;
    if (nav.globalPrivacyControl === true) return true;
    if (nav.doNotTrack === "1" || (w as any)?.doNotTrack === "1") return true;
    return false;
  } catch {
    return false;
  }
}

/** Evaluates a config-provided JS snippet in the host page. try/catch on both
 *  compile and run; any failure counts as "no". */
function evalExpr(expr: string): unknown {
  try {
    // eslint-disable-next-line no-new-func
    return new Function("return (" + expr + ")")();
  } catch {
    return undefined;
  }
}

// ── built-in CMP shims ──────────────────────────────────────────────────
//
// Each shim: detect() = is this CMP on the page; start(apply) = read the
// current state, subscribe to changes, return an unsubscribe. `apply(null)`
// means "present but undecided" — the adapter stays cookieless until a real
// signal arrives.

interface CmpShim {
  name: string;
  detect(): boolean;
  start(apply: (granted: boolean | null) => void): () => void;
}

function onWindowEvent(name: string, handler: () => void): () => void {
  const w = win();
  try {
    w?.addEventListener(name, handler);
  } catch {
    return () => undefined;
  }
  return () => {
    try {
      w?.removeEventListener(name, handler);
    } catch {
      /* noop */
    }
  };
}

function buildBuiltinShims(config: ConsentConfig): CmpShim[] {
  const w = () => win();

  const tcf: CmpShim = {
    name: "cmp:tcf",
    detect: () => {
      try {
        return typeof w()?.__tcfapi === "function";
      } catch {
        return false;
      }
    },
    start: (apply) => {
      const purposes = config.tcfPurposes && config.tcfPurposes.length > 0
        ? config.tcfPurposes
        : [8, 9, 10];
      let removed = false;
      let listenerId: number | undefined;
      try {
        w()?.__tcfapi("addEventListener", 2, (tcData: any, success: boolean) => {
          if (removed || !success || !tcData) return;
          if (typeof tcData.listenerId === "number") listenerId = tcData.listenerId;
          const status = tcData.eventStatus;
          if (status !== "tcloaded" && status !== "useractioncomplete") return;
          try {
            const consents = tcData.purpose?.consents ?? {};
            apply(purposes.every((p) => consents[p] === true));
          } catch {
            apply(false);
          }
        });
      } catch {
        apply(null);
      }
      return () => {
        removed = true;
        try {
          if (listenerId !== undefined) {
            w()?.__tcfapi("removeEventListener", 2, () => undefined, listenerId);
          }
        } catch {
          /* noop */
        }
      };
    },
  };

  // Google Consent Mode v2 — scan dataLayer for consent commands, then wrap
  // push to hear updates. gtag() pushes `arguments` objects, so entries are
  // array-like, not arrays.
  const readGoogleConsent = (): boolean | null => {
    try {
      const dl = w()?.dataLayer;
      if (!Array.isArray(dl)) return null;
      let value: boolean | null = null;
      for (const entry of dl) {
        try {
          if (!entry || entry[0] !== "consent") continue;
          const params = entry[2];
          const v = params?.analytics_storage;
          if (v === "granted") value = true;
          else if (v === "denied") value = false;
        } catch {
          /* skip malformed entry */
        }
      }
      return value;
    } catch {
      return null;
    }
  };

  const googleConsentMode: CmpShim = {
    name: "cmp:google-consent-mode",
    detect: () => readGoogleConsent() !== null,
    start: (apply) => {
      apply(readGoogleConsent());
      const wRef = w();
      let unpatch = () => undefined as void;
      try {
        const dl = wRef?.dataLayer;
        if (Array.isArray(dl)) {
          const original = dl.push.bind(dl);
          dl.push = function patchedPush(...args: unknown[]) {
            const result = original(...(args as [unknown]));
            try {
              for (const entry of args as any[]) {
                if (entry && entry[0] === "consent") apply(readGoogleConsent());
              }
            } catch {
              /* noop */
            }
            return result;
          } as typeof dl.push;
          unpatch = () => {
            try {
              dl.push = original;
            } catch {
              /* noop */
            }
          };
        }
      } catch {
        /* noop */
      }
      return unpatch;
    },
  };

  const readCookiebot = (): boolean | null => {
    try {
      const cb = w()?.Cookiebot;
      if (!cb || typeof cb.consent !== "object" || cb.consent === null) return null;
      return cb.consent.statistics === true;
    } catch {
      return null;
    }
  };

  const cookiebot: CmpShim = {
    name: "cmp:cookiebot",
    detect: () => {
      try {
        return !!w()?.Cookiebot;
      } catch {
        return false;
      }
    },
    start: (apply) => {
      apply(readCookiebot());
      const handler = () => apply(readCookiebot());
      const offs = [
        onWindowEvent("CookiebotOnConsentReady", handler),
        onWindowEvent("CookiebotOnAccept", handler),
        onWindowEvent("CookiebotOnDecline", handler),
      ];
      return () => offs.forEach((off) => off());
    },
  };

  const readCookieInformation = (): boolean | null => {
    try {
      const ci = w()?.CookieInformation;
      if (!ci || typeof ci.getConsentGivenFor !== "function") return null;
      return ci.getConsentGivenFor("cookie_cat_statistic") === true;
    } catch {
      return null;
    }
  };

  const cookieInformation: CmpShim = {
    name: "cmp:cookie-information",
    detect: () => {
      try {
        const ci = w()?.CookieInformation;
        return !!ci && typeof ci.getConsentGivenFor === "function";
      } catch {
        return false;
      }
    },
    start: (apply) => {
      apply(readCookieInformation());
      const off = onWindowEvent("CookieInformationConsentGiven", () =>
        apply(readCookieInformation()),
      );
      return off;
    },
  };

  const readOneTrust = (): boolean | null => {
    try {
      const groups = w()?.OnetrustActiveGroups;
      if (typeof groups !== "string") return null;
      return groups.split(",").map((g) => g.trim()).includes("C0002");
    } catch {
      return null;
    }
  };

  const oneTrust: CmpShim = {
    name: "cmp:onetrust",
    detect: () => {
      try {
        const wRef = w();
        return typeof wRef?.OnetrustActiveGroups === "string" || !!wRef?.OneTrust;
      } catch {
        return false;
      }
    },
    start: (apply) => {
      apply(readOneTrust());
      const handler = () => apply(readOneTrust());
      const offWin = onWindowEvent("OneTrustGroupsUpdated", handler);
      // Some OneTrust versions dispatch on document instead of window.
      let offDoc = () => undefined as void;
      try {
        document.addEventListener("OneTrustGroupsUpdated", handler);
        offDoc = () => {
          try {
            document.removeEventListener("OneTrustGroupsUpdated", handler);
          } catch {
            /* noop */
          }
        };
      } catch {
        /* noop */
      }
      return () => {
        offWin();
        offDoc();
      };
    },
  };

  // Usercentrics — best effort per design; the v2 browser API surface varies.
  const readUsercentrics = (): boolean | null => {
    try {
      const uc = w()?.UC_UI;
      if (!uc || typeof uc.getServicesBaseInfo !== "function") return null;
      const services: any[] = uc.getServicesBaseInfo() ?? [];
      const analytical = services.filter((s) => {
        const slug = String(s?.categorySlug ?? "").toLowerCase();
        return slug.includes("analy") || slug.includes("statis");
      });
      if (analytical.length === 0) return null;
      return analytical.some((s) => s?.consent?.status === true);
    } catch {
      return null;
    }
  };

  const usercentrics: CmpShim = {
    name: "cmp:usercentrics",
    detect: () => {
      try {
        return !!w()?.UC_UI;
      } catch {
        return false;
      }
    },
    start: (apply) => {
      apply(readUsercentrics());
      const off = onWindowEvent("UC_UI_INITIALIZED", () => apply(readUsercentrics()));
      return off;
    },
  };

  return [tcf, googleConsentMode, cookiebot, cookieInformation, oneTrust, usercentrics];
}

/** Wraps one bootstrap-config custom adapter as a shim. */
function buildCustomShim(def: CustomAdapterConfig): CmpShim | null {
  if (!def || typeof def.detectExpr !== "string" || typeof def.readExpr !== "string") return null;
  // Only statistics-like adapters affect the tracker; an unset category is
  // treated as statistics (the common case for bespoke banners).
  const category = String(def.category ?? "statistics").toLowerCase();
  if (!(category.includes("statis") || category.includes("analy"))) return null;
  return {
    name: `cmp:custom:${def.name || "unnamed"}`,
    detect: () => !!evalExpr(def.detectExpr),
    start: (apply) => {
      apply(!!evalExpr(def.readExpr));
      if (!def.updateEvent) return () => undefined;
      return onWindowEvent(def.updateEvent, () => apply(!!evalExpr(def.readExpr)));
    },
  };
}

// ── the adapter ─────────────────────────────────────────────────────────

export function createConsentAdapter(opts: ConsentAdapterOptions): ConsentAdapter {
  const config: ConsentConfig = opts.config ?? {};
  const respectGpc = config.respectGpc !== false; // default true
  const gpcCapped = respectGpc && isGpcOrDntActive();

  let state: ConsentState = {
    status: "cookieless",
    tier: 1,
    source: "default",
  };
  let destroyed = false;
  let banner: BannerHandle | null = null;
  let activeShimStop: (() => void) | null = null;
  const retryTimers: ReturnType<typeof setTimeout>[] = [];
  const listeners = new Set<(s: ConsentState) => void>();
  if (opts.onChange) listeners.add(opts.onChange);

  const emit = () => {
    for (const l of listeners) {
      try {
        l(state);
      } catch {
        /* listener errors must not break consent flow */
      }
    }
  };

  const apply = (status: ConsentStatus, source: string, tier?: 0 | 1 | 2) => {
    if (destroyed) return;
    let nextStatus = status;
    let nextSource = source;
    if (gpcCapped && status === "granted") {
      nextStatus = "cookieless";
      nextSource = "gpc";
    }
    const nextTier: 0 | 1 | 2 = tier !== undefined ? tier : nextStatus === "granted" ? 2 : 1;

    // Visitor id lifecycle: create/persist on grant, delete on anything else.
    if (nextStatus === "granted") {
      if (!safeGetItem(VISITOR_ID_KEY)) safeSetItem(VISITOR_ID_KEY, newUuid());
    } else {
      safeRemoveItem(VISITOR_ID_KEY);
    }

    if (state.status === nextStatus && state.tier === nextTier && state.source === nextSource) return;
    state = { status: nextStatus, tier: nextTier, source: nextSource };
    emit();
  };

  const applyFromCmp = (shimName: string) => (granted: boolean | null) => {
    if (granted === null) return; // present but undecided → stay as-is (cookieless baseline)
    apply(granted ? "granted" : "denied", shimName);
  };

  const startShim = (shim: CmpShim) => {
    try {
      activeShimStop = shim.start(applyFromCmp(shim.name));
    } catch {
      activeShimStop = null;
    }
  };

  const showBannerFallback = () => {
    // A previously stored explicit choice skips re-prompting.
    const stored = readStoredBannerChoice();
    if (stored) {
      apply(stored.tier === "full" ? "granted" : "denied", "own-banner:stored");
      return;
    }
    if (gpcCapped) {
      // Never prompt a visitor who broadcast an opt-out signal.
      apply("cookieless", "gpc");
      return;
    }
    try {
      banner = showConsentBanner({
        container: opts.bannerContainer ?? null,
        style: config.bannerStyle ?? "minimal",
        language: opts.language,
        privacyUrl: config.privacyUrl,
        strings: config.strings,
        version: config.version,
        apiBase: opts.apiBase,
        sessionId: opts.sessionId ?? null,
        getVisitorId: () => getVisitorId(),
        onChoice: (tier) => {
          banner = null;
          apply(tier === "full" ? "granted" : "denied", "own-banner");
        },
      });
    } catch {
      apply("cookieless", "fallback:banner-failed");
    }
  };

  const runFallback = () => {
    const fallback = config.fallback ?? "banner";
    if (fallback === "banner") showBannerFallback();
    else if (fallback === "essential") apply("denied", "fallback:essential", 0);
    else apply("cookieless", "fallback:cookieless");
  };

  const shims: CmpShim[] = [
    ...buildBuiltinShims(config),
    ...(config.customAdapters ?? [])
      .map(buildCustomShim)
      .filter((s): s is CmpShim => s !== null),
  ];

  /** One detection pass over the shim list; true when a CMP was found. */
  const tryDetect = (filterName?: string): boolean => {
    for (const shim of shims) {
      if (filterName && !shim.name.endsWith(`:${filterName}`) && shim.name !== `cmp:${filterName}`) {
        continue;
      }
      let present = false;
      try {
        present = shim.detect();
      } catch {
        present = false;
      }
      if (present) {
        startShim(shim);
        return true;
      }
    }
    return false;
  };

  /** Detect now, then retry on the grace schedule, then fall back. */
  const detectWithRetries = (filterName?: string) => {
    if (tryDetect(filterName)) return;
    let attempt = 0;
    const schedule = () => {
      if (attempt >= DETECT_RETRY_AT_MS.length) {
        runFallback();
        return;
      }
      const prev = attempt === 0 ? 0 : DETECT_RETRY_AT_MS[attempt - 1];
      const delay = DETECT_RETRY_AT_MS[attempt] - prev;
      attempt += 1;
      const t = setTimeout(() => {
        if (destroyed) return;
        if (tryDetect(filterName)) return;
        schedule();
      }, delay);
      retryTimers.push(t);
    };
    schedule();
  };

  const getVisitorId = (): string | null => {
    if (state.status !== "granted") return null;
    return safeGetItem(VISITOR_ID_KEY);
  };

  // ── resolve initial state ─────────────────────────────────────────────
  // A per-embed override (data-3dconfig-consent) is the most specific and wins;
  // otherwise the server-provided mode (the dashboard per-tool / deployment
  // consent setting) decides. "auto"/empty/unknown means run full CMP detection
  // with the configured fallback — the default for tools with no override.
  const explicit = (opts.explicitOverride ?? "").trim().toLowerCase();
  const serverMode = (config.mode ?? "").trim().toLowerCase();
  const directive = explicit || serverMode;
  const source = explicit ? "override" : "config";
  if (directive === "granted") {
    apply("granted", source);
  } else if (directive === "denied") {
    apply("denied", source);
  } else if (directive === "cookieless") {
    apply("cookieless", source);
  } else if (directive === "essential") {
    apply("denied", source, 0);
  } else if (directive === "banner") {
    showBannerFallback();
  } else if (directive.startsWith("cmp:")) {
    detectWithRetries(directive.slice(4));
  } else {
    // "auto", empty, or unknown value → full detection.
    detectWithRetries();
  }

  return {
    getState: () => state,
    getVisitorId,
    setConsent: (status, source) => {
      // A programmatic choice replaces whatever a CMP said; also dismisses our
      // banner if it is still up.
      if (banner) {
        banner.destroy();
        banner = null;
      }
      apply(status, source ?? "api");
    },
    onChange: (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    destroy: () => {
      destroyed = true;
      for (const t of retryTimers) clearTimeout(t);
      retryTimers.length = 0;
      if (banner) {
        banner.destroy();
        banner = null;
      }
      if (activeShimStop) {
        try {
          activeShimStop();
        } catch {
          /* noop */
        }
        activeShimStop = null;
      }
      listeners.clear();
    },
  };
}

export { newUuid };
